import React, { Component } from "react";
import { RTRegistrationContext } from "@my-dld/contexts/RTRegistrationContext";
import { showErrorMessage } from "@my-dld/utils/UIUtils";
import Strings from "@my-dld/assets/strings";
import { RTRegistrationProps, TrackIndex } from "./RTRegistration";
import { I8N, StringUtil } from "@eres/core";
import Dropzone from "react-dropzone";
import { getTabuDocDate, getTabuDocName, getTabuDocSize, getTabuDocType, TabuDocTypes } from "@my-dld/utils/TabuUtils";
import CheckBoxField from "@my-dld/components/Form/CheckBoxField";
import DocumentInputField from "@my-dld/components/Form/DocumentInputField";
import { ProcedureDoc, ProcedureUploadDoc } from "@eres/rtregistrationapi/entities";

interface State {
	docs: ProcedureDoc[];
	disclaimer: boolean;
	error: { [ k: string ]: string };
}

class ProcedureDocuments extends Component<RTRegistrationProps, State> {
	static contextType = RTRegistrationContext;
	context!: React.ContextType<typeof RTRegistrationContext>;

	constructor( props: RTRegistrationProps ) {
		super( props );
		this.state = {
			docs: props.procedure.Documents,
			disclaimer: false,
			error: {},
		};
	}

	render() {
		const { docs, error } = this.state;
		return <div className="procedure-document" id="procedure_document">
			<div className="card">
				<div className="card-header">
					<span>{ Strings.rt_upload_documents_and_submit }</span>
				</div>
				<div className="card-body">
					<RenderProcedureDocs
						procDocs={ docs }
						onDropRejected={ this.onDropRejected }
						onDrop={ this.onDrop }
						onDelete={ this.onDelete }
						docsError={ error }
						showMandatory={ true }/>
					<div className="form-group">
						<CheckBoxField
							id={ "disclaimer" }
							holder={ this }
							required={ true }
							label={ Strings.rt_disclaimer }
							error={ error.disclaimer }/>
					</div>
				</div>
			</div>
			<div className="form-group btn-party-action">
				<button className="btn btn_style1 btn-grey" onClick={ this.onBack }>{ Strings.back }</button>
				<button
					className="btn btn_1 btn_style1" type="submit"
					onClick={ this.onSubmit }>{ Strings.submit_label }</button>
			</div>
		</div>;
	}

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private onDrop = ( doc: ProcedureDoc, files: File[] ) => {
		files.filter( x => x.type.startsWith( "image/" ) || x.type.startsWith( "application/pdf" ) )
		.forEach( file => this.addFile( doc, file ) );
	};

	private addFile( uploadDoc: ProcedureDoc, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			const binaryStr = reader.result;
			uploadDoc.doc = {
				identity: uploadDoc.DocumentName,
				localData: binaryStr as string,
				fileName: file.name,
				fileSize: file.size,
				type: file.type,
			};
			uploadDoc.Path = undefined;
			let error = this.state.error;
			if ( error[ uploadDoc.DocumentName ] )
				delete error[ uploadDoc.DocumentName ];
			this.setState( {
				error: error,
				docs: this.state.docs.map( ( doc: ProcedureDoc ) => {
					return ( uploadDoc.DocumentName === doc.DocumentName ) ? uploadDoc : doc;
				} ),
			} );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( identity: any ) => {
		this.setState( {
			docs: this.state.docs.map( ( procDoc: ProcedureDoc ) => {
				if ( procDoc.DocumentName === identity ) {
					procDoc.Path = undefined;
					procDoc.doc = undefined;
					return procDoc;
				}
				return procDoc;
			} ),
		} );
	};

	private onBack = () => {
		const procedure = this.props.holder.state.procedure;
		procedure.Documents = this.state.docs;
		this.props.onPrevious( TrackIndex.PROCEDURE_DOCUMENTS );
	};

	private onSubmit = () => {
		if ( this.isValid() ) {
			const procedure = this.props.holder.state.procedure;
			procedure.Documents = this.state.docs;
			this.props.onNext( TrackIndex.PROCEDURE_DOCUMENTS );
		}
	};

	private isValid() {
		let isValid = true;
		const error: { [ k: string ]: string } = {};
		const { docs, disclaimer } = this.state;
		docs.forEach( ( doc: ProcedureDoc ) => {
			if ( doc.IsRequired && doc.doc == undefined ) {
				error[ doc.DocumentName ] = Strings.required;
				isValid = false;
			}
		} );
		if ( !disclaimer ) {
			error.disclaimer = Strings.required;
			isValid = false;
		}
		this.setState( {
			error: error,
		} );
		return isValid;
	}
}

export function RenderProcedureDocs( props: {
	procDocs: ProcedureDoc[], onDropRejected: () => void,
	onDrop: ( ( procDoc: ProcedureDoc, files: File[] ) => void ), onDelete: ( identity: any ) => void,
	errorMsg?: string, docsError?: any, showMandatory?: boolean
} ) {
	const { procDocs, onDropRejected, onDrop, onDelete, errorMsg, docsError, showMandatory } = props;
	return ( <div className="row">
		<div className="col-md-12 font-weight-bold mb-3">
			Documents
			<span className="d-flex">
                { showMandatory && <small>{ Strings.mandatory_fields_note }</small> }
            </span>
			<span className="d-flex">
                <small>{ Strings.tabu_upload_documents_note }</small>
            </span>
		</div>
		<div className="col-md-12">
			{ errorMsg && <div className="alert alert-danger">{ errorMsg }</div> }
		</div>
		{ procDocs.map( ( procDoc: ProcedureDoc, index: number ) => {
			let title = I8N.isRTL() ? procDoc.DocumentNameAr : procDoc.DocumentName;
			return <div key={ "docs_" + index } className="col-md-12">
				<Dropzone
					multiple={ false }
					maxSize={ 2 * 1024 * 1000 }
					onDropRejected={ onDropRejected }
					onDrop={ ( files: File[] ) => {
						onDrop( procDoc, files );
					} }
					accept={ [ "image/png", "image/jpeg", "application/pdf" ] }>
					{ ( { getRootProps, getInputProps } ) => (
						<DocumentInputField { ...getRootProps() }
						                    dropZoneInputProps={ getInputProps() }
						                    required={ procDoc.IsRequired }
						                    label={ title }
						                    error={ docsError ? docsError[ procDoc.DocumentName ] : undefined }
						                    isDocsAdded={ procDoc.doc !== undefined }/>
					) }
				</Dropzone>
				{ procDoc.doc && <RenderProcedureDoc
					doc={ procDoc.doc } onDelete={ ( identity: any ) => {
					onDelete( identity );
				} }/> }
			</div>;
		} )
		}
	</div> );
}

export function RenderProcedureDoc( props: { doc: ProcedureUploadDoc, onDelete: ( identity: any ) => void } ) {
	const { doc, onDelete } = props;
	let docType = getTabuDocType( doc );
	let preview = ( docType === TabuDocTypes.PDF ) ? <i className="far fa-file-pdf"/> : getImagePreview( doc );
	return <div className={ "mb-2" }>
		<table className="table table-bordered table-condensed table-striped table-responsive-sm upload-table">
			<thead>
			<tr>
				<th>{ Strings.file }</th>
				<th>{ Strings.name }</th>
				<th>{ Strings.size }</th>
				<th>{ Strings.vid_date }</th>
				<th>{ Strings.action }</th>
			</tr>
			</thead>
			<tbody>
			<tr>
				<td data-label={ Strings.file }>{ preview }</td>
				<td data-label={ Strings.name }>{ getTabuDocName( doc ) }</td>
				<td data-label={ Strings.size }>{ getTabuDocSize( doc ) + " " + Strings.size_mb }</td>
				<td data-label={ Strings.vid_date }>{ getTabuDocDate( doc ) }</td>
				<td data-label={ Strings.action }>
					<button
						className="btn btn-grey" onClick={ () => {
						onDelete( doc.identity );
					} }><i className="far fa-trash-alt"/>{ Strings.delete }
					</button>
				</td>
			</tr>
			</tbody>
		</table>
	</div>;
}

function getImagePreview( tabuDoc: ProcedureUploadDoc ) {
	let source = ( tabuDoc.localData && !StringUtil.isEmptyStringOrNull( tabuDoc.localData ) ) ?
		tabuDoc.localData : tabuDoc.fileUrl;
	return <img src={ source } style={ { height: 40, width: 40 } } alt={ "uploaded_image" }/>;
}

export default ProcedureDocuments;
